import { useMemo } from "react";
import { cn } from "@/lib/utils";

/**
 * Small, memoised chip strip for `semantic_features` — a comma-joined
 * string of PH constraints (e.g. "flood-free, 40-ft truck accessible,
 * 3-phase electricity"). Splits, trims and truncates the list. Rendered
 * as flat inline pills.
 *
 * `variant`:
 *   "muted"  — gray pills (listings)
 *   "navy"   — navy-soft pills (buyer constraints on requirements/matches)
 *   "listing-mini" / "requirement-mini" — smaller sizes for the Matches page cards
 */
export function SemanticFeatureChips({
  value,
  limit = 6,
  variant = "muted",
  testId,
  keyPrefix = "sf",
}) {
  const items = useMemo(() => {
    if (!value) return [];
    return String(value)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, limit);
  }, [value, limit]);

  if (items.length === 0) return null;

  const classes = {
    muted:
      "inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-600",
    navy:
      "inline-flex items-center rounded-md bg-brand-navy-soft px-2 py-0.5 text-[11px] font-medium text-brand-navy",
    "listing-mini":
      "rounded bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-600",
    "requirement-mini":
      "rounded bg-brand-navy-soft px-1.5 py-0.5 text-[10px] font-medium text-brand-navy",
  };
  const chipClass = classes[variant] || classes.muted;

  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {items.map((feat, i) => (
        <span key={`${keyPrefix}-${i}`} className={cn(chipClass)}>
          {feat}
        </span>
      ))}
    </div>
  );
}
